// Layout.jsx -- Sprint Hub S114
// Shell: 220px Sidebar + 52px Topbar + content area
// Topbar: greeting + date left, search/theme/avatar-dropdown right
// Sidebar: dynamic agent domains from prefs
// ASCII only. No Unicode. var declarations only.
import React, { useState, useEffect } from 'react';
import { ThemeContext, THEMES, FONT, UiModeContext } from '../lib/theme.js';
import HelpPill from './HelpPill.jsx';
import HomeBrief from './hub-view/HomeBrief.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import SearchOverlay from './SearchOverlay.jsx';
import NotificationBell from './NotificationBell.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';
import AgentOverlay from './AgentOverlay.jsx';
import GodMode from './GodMode.jsx';
import CallWhisperer from './CallWhisperer.jsx';
import { useIsMobile, injectMobileStyles } from '../utils/mobileLayout.js';
import DisplayDrawer, { applyDisplaySettings } from './DisplayDrawer.jsx';
import BugPanel from './BugPanel.jsx';
import UserMenu, { MenuItem, TenantItem, TEAM } from './UserMenu.jsx';

var API_BASE = '/api';
var AARON_EMAIL = 'aaron@sprintmode.co';

var DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getGreeting() {
  var h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getDateStr() {
  var d = new Date();
  return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate();
}

function parseDashboardTheme(prefs) {
  try {
    var layout = JSON.parse((prefs && prefs.dashboard_layout_json) || '{}');
    return layout.theme || 'light';
  } catch(e) { return 'light'; }
}

function useSearchOverlay() {
  var _s = useState(false);
  var open = _s[0]; var setOpen = _s[1];

  useEffect(function() {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, []);

  return { open: open, setOpen: setOpen };
}

// Agent shortcut hook -- Cmd+1 through Cmd+4 navigate directly to agent views
var AGENT_SHORTCUT_MAP = [
  { key: '1', agentId: 'agent_cfo'                 },
  { key: '2', agentId: 'agent_sales_director'       },
  { key: '3', agentId: 'agent_engineering_manager'  },
  { key: '4', agentId: 'agent_bookkeeper'           },
];

function useAgentShortcuts(navigate) {
  useEffect(function() {
    function onKey(e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      for (var i = 0; i < AGENT_SHORTCUT_MAP.length; i++) {
        if (e.key === AGENT_SHORTCUT_MAP[i].key) {
          e.preventDefault();
          navigate('/agent/' + AGENT_SHORTCUT_MAP[i].agentId);
          return;
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [navigate]);
}

function useUserPreferences(user, impEmail) {
  var _s = useState(null);
  var prefs = _s[0]; var setPrefs = _s[1];
  useEffect(function() {
    if (!user) return;
    var url = API_BASE + '/me/preferences';
    if (impEmail) url += '?impersonate=' + encodeURIComponent(impEmail);
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(d) { if (d.data) setPrefs(d.data); })
      .catch(function() {});
  }, [user, impEmail]);
  return prefs;
}

var EXTERNAL_LINKS = {
  console:    'https://console.tryhub.ai',
  developers: 'https://developers.tryhub.ai',
  docs:       'https://docs.tryhub.ai',
  legal:      'https://heyhub.ai/legal/terms',
  myHub:      'https://app.tryhub.ai'
};


function getImpEmail() {
  try { return window.localStorage.getItem('hub_impersonate') || ''; } catch(e) { return ''; }
}

export default function Layout(props) {
  var children = props.children;
  var navigate = useNavigate();
  var location = useLocation();

  // Chromeless routes: /present, /companion, or any route with ?popout=true.
  // These are spatial / phone-companion surfaces -- global overlays (BugPanel, DisplayDrawer) must not leak.
  var isChromelessRoute = (function() {
    if (location.pathname === '/present' || location.pathname === '/companion') return true;
    try { return new URLSearchParams(window.location.search || '').get('popout') === 'true'; } catch(e) { return false; }
  })();

  var _user = useState(null);
  var user = _user[0]; var setUser = _user[1];
  var _askOpen = useState(false);
  var askOpen = _askOpen[0]; var setAskOpen = _askOpen[1];
  var _bugPanelOpen = useState(false);
  var bugPanelOpen = _bugPanelOpen[0]; var setBugPanelOpen = _bugPanelOpen[1];
  // Sidebar: 'open' (220px), 'rail' (48px icons only), 'closed' (mobile only)
  var _sidebarMode = useState(function() {
    return window.innerWidth > 768 ? 'open' : 'closed';
  });
  var sidebarMode = _sidebarMode[0]; var setSidebarMode = _sidebarMode[1];
  var sidebarOpen = sidebarMode === 'open'; // backward compat for Sidebar component
  var _themeOverride = useState(null);
  var themeOverride = _themeOverride[0]; var setThemeOverride = _themeOverride[1];
  var _compose = useState(null);
  var composeData = _compose[0]; var setComposeData = _compose[1];
  var _cTo = useState(''); var cTo = _cTo[0]; var setCTo = _cTo[1];
  var _cSubj = useState(''); var cSubj = _cSubj[0]; var setCSubj = _cSubj[1];
  var _cBody = useState(''); var cBody = _cBody[0]; var setCBody = _cBody[1];
  var _cSending = useState(false); var cSending = _cSending[0]; var setCSSending = _cSending[1];
  var _flashMsg = useState(''); var flashMsg = _flashMsg[0]; var setFlashMsg = _flashMsg[1];
  var _avatarOpen = useState(false);
  var avatarOpen = _avatarOpen[0]; var setAvatarOpen = _avatarOpen[1];
  var _alertCounts = useState({ platform: 0, dev: 0 });
  var alertCounts = _alertCounts[0]; var setAlertCounts = _alertCounts[1];
  var _impEmail = useState(getImpEmail);
  var impersonateEmail = _impEmail[0]; var setImpersonateEmail = _impEmail[1];
  var _impOpen = useState(false);
  var impOpen = _impOpen[0]; var setImpOpen = _impOpen[1];
  var _tenants = useState([]);
  var tenants = _tenants[0]; var setTenants = _tenants[1];
  var _currentTenant = useState(function() { try { return localStorage.getItem('hub_tenant') || 'sprint_mode'; } catch(e) { return 'sprint_mode'; } });
  var currentTenant = _currentTenant[0]; var setCurrentTenant = _currentTenant[1];
  var _tenantOpen = useState(false);
  var tenantOpen = _tenantOpen[0]; var setTenantOpen = _tenantOpen[1];
  var _whispererActive = useState(false);
  var whispererActive = _whispererActive[0]; var setWhispererActive = _whispererActive[1];
  var _activeCallId = useState(null);
  var activeCallId = _activeCallId[0]; var setActiveCallId = _activeCallId[1];
  var _displayVer = useState(0);
  var displayVer = _displayVer[0]; var setDisplayVer = _displayVer[1];
  var _statusData = useState(null); var statusData = _statusData[0]; var setStatusData = _statusData[1];
  var _statusDismissed = useState(false); var statusDismissed = _statusDismissed[0]; var setStatusDismissed = _statusDismissed[1];
  var _statusExpanded = useState(false); var statusExpanded = _statusExpanded[0]; var setStatusExpanded = _statusExpanded[1];
  var _hubList = useState([]); var hubList = _hubList[0]; var setHubList = _hubList[1];
  var _currentHub = useState('sprint_mode'); var currentHub = _currentHub[0]; var setCurrentHub = _currentHub[1];
  var _uiMode = useState('standard'); var uiMode = _uiMode[0]; var setUiMode = _uiMode[1];

  var search = useSearchOverlay();
  useAgentShortcuts(navigate);

  // Clear dismiss flag on load so banner reappears on every reload
  useEffect(function() {
    try { sessionStorage.removeItem('hub_status_dismissed'); } catch(e) {}
  }, []);

  // Hub list + current hub (PH-S5)
  useEffect(function() {
    fetch('/api/hub/list').then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok && d.data) setHubList(d.data);
    }).catch(function() {});
    fetch('/api/hub/current').then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok && d.data) setCurrentHub(d.data.hub_id);
    }).catch(function() {});
  }, []);

  useEffect(function() {
    fetch('/api/admin/lenses/attention-count')
      .then(function(r) { return r.json(); })
      .then(function(j) { if (j.ok && j.data) setAlertCounts(j.data); })
      .catch(function() {});
  }, []);

  useEffect(function() {
    function fetchStatus() {
      fetch('/api/status').then(function(r) { return r.ok ? r.json() : null; }).then(function(d) {
        if (d && d.ok) {
          // If status recovered to operational, clear dismiss so banner reappears if it degrades again
          if (d.data && d.data.overall === 'operational') {
            try { sessionStorage.removeItem('hub_status_dismissed'); } catch(e) {}
            setStatusDismissed(false);
          }
          setStatusData(d.data);
        }
      }).catch(function() {});
    }
    fetchStatus();
    var interval = setInterval(fetchStatus, 60000);
    return function() { clearInterval(interval); };
  }, []);

  useEffect(function() {
    function handleSidebarKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        setSidebarMode(function(prev) {
          if (window.innerWidth <= 768) {
            return prev === 'open' ? 'closed' : 'open';
          }
          return prev === 'open' ? 'rail' : 'open';
        });
      }
    }
    window.addEventListener('keydown', handleSidebarKey);
    return function() { window.removeEventListener('keydown', handleSidebarKey); };
  }, []);

  useEffect(function() {
    function handleNewThreadKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'o' || e.key === 'O')) {
        // Skip if focus is in a text input/textarea/contenteditable
        var tag = document.activeElement ? document.activeElement.tagName : '';
        var editable = document.activeElement && document.activeElement.isContentEditable;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) return;
        e.preventDefault();
        var hash = window.location.pathname + (window.location.search || '');
        var agentMatch = hash.match(/\/agent\/([a-z_0-9]+)/);
        var currentAgent = agentMatch ? agentMatch[1] : '';
        if (currentAgent) {
          fetch('/api/agents/' + currentAgent + '/threads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: 'New thread', visibility: 'shared' })
          }).then(function(r) { return r.json(); }).then(function(d) {
            if (d.ok && d.data && d.data.id) {
              window.location.href = '/agent/' + currentAgent + '?thread=' + d.data.id;
            } else {
              window.location.href = '/agent/' + currentAgent + '?newThread=true';
            }
          }).catch(function() {
            window.location.href = '/agent/' + currentAgent + '?newThread=true';
          });
        } else {
          fetch('/api/threads?limit=1').then(function(r) { return r.json(); }).then(function(d) {
            var fallback = 'agent_engineering_manager';
            if (d.ok && d.data) {
              var items = Array.isArray(d.data.threads) ? d.data.threads : [];
              if (items.length > 0 && items[0].agent_id) fallback = items[0].agent_id;
            }
            window.location.href = '/agent/' + fallback + '?newThread=true';
          }).catch(function() {
            window.location.href = '/agent/agent_engineering_manager?newThread=true';
          });
        }
      }
    }
    document.addEventListener('keydown', handleNewThreadKey);
    return function() { document.removeEventListener('keydown', handleNewThreadKey); };
  }, []);

  var prefs = useUserPreferences(user, impersonateEmail);
  var isMobile = useIsMobile();

  var prefsTheme = parseDashboardTheme(prefs);
  var themeName = themeOverride || prefsTheme;
  var theme = THEMES[themeName] || THEMES.light;

  useEffect(function() {
    document.body.classList.toggle('dark-mode', themeName === 'dark');
  }, [themeName]);

  useEffect(function() { injectMobileStyles(); }, []);

  useEffect(function() {
    if (isMobile) setSidebarMode('closed');
  }, [isMobile]);

  useEffect(function() {
    fetch(API_BASE + '/me')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.data) {
          setUser(d.data);
          if (d.data.ui_mode === 'senior') setUiMode('senior');
        }
      })
      .catch(function() {});
  }, []);

  useEffect(function() {
    if (prefs && prefs.default_view && location.pathname === '/') {
      navigate('/' + prefs.default_view);
    }
  }, [prefs, location.pathname]);

  useEffect(function() {
    if (!prefs) return;
    try {
      var ds = prefs.display_settings;
      if (ds) {
        var parsed = typeof ds === 'string' ? JSON.parse(ds) : ds;
        applyDisplaySettings(parsed);
      }
    } catch(e) {}
  }, [prefs]);

  useEffect(function() {
    function onCompose(e) { setComposeData(e.detail || {}); }
    window.addEventListener('hub:compose', onCompose);
    return function() { window.removeEventListener('hub:compose', onCompose); };
  }, []);

  useEffect(function() {
    function onDisplayChanged() { setDisplayVer(function(v) { return v + 1; }); }
    window.addEventListener('hub-display-changed', onDisplayChanged);
    return function() { window.removeEventListener('hub-display-changed', onDisplayChanged); };
  }, []);

  useEffect(function() {
    if (composeData) {
      setCTo(composeData.to || '');
      setCSubj(composeData.subject || '');
      setCBody(composeData.body || '');
    }
  }, [composeData]);

  useEffect(function() {
    function onTask(e) {
      var d = e.detail || {};
      fetch(API_BASE + '/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(d)
      }).catch(function() {});
    }
    window.addEventListener('hub:create-task', onTask);
    return function() { window.removeEventListener('hub:create-task', onTask); };
  }, []);

  useEffect(function() {
    fetch('/api/tenants').then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok) setTenants(d.data || []);
    }).catch(function() {});
  }, []);

  function switchTenant(tid) {
    try { localStorage.setItem('hub_tenant', tid); } catch(e) {}
    setCurrentTenant(tid);
    setTenantOpen(false);
    window.location.reload();
  }

  // Hub context switch (PH-S5)
  function switchHub(hubId) {
    fetch('/api/hub/switch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hub_id: hubId })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok) {
        setCurrentHub(hubId);
        window.location.href = '/brief';
      }
    }).catch(function() {});
  }

  function flash(msg) {
    setFlashMsg(msg);
    setTimeout(function() { setFlashMsg(''); }, 3000);
  }

  function handleComposeSend() {
    if (!cTo || !cSubj) return;
    setCSSending(true);
    fetch('/api/outbound/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: cTo, subject: cSubj, body: cBody })
    }).then(function(r) { return r.json(); })
      .then(function() { setComposeData(null); flash('Email queued.'); })
      .catch(function() { flash('Send failed -- try again.'); })
      .finally(function() { setCSSending(false); });
  }

  function toggleTheme() {
    setThemeOverride(themeName === 'dark' ? 'light' : 'dark');
  }

  function handleDropdownNav(path) {
    setAvatarOpen(false);
    navigate(path);
  }

  function handleImpersonate(email) {
    var newEmail = (email === AARON_EMAIL || email === '') ? '' : email;
    try {
      if (newEmail) window.localStorage.setItem('hub_impersonate', newEmail);
      else window.localStorage.removeItem('hub_impersonate');
    } catch(e) {}
    setImpersonateEmail(newEmail);
    setImpOpen(false);
    window.dispatchEvent(new Event('hub-impersonate-changed'));
  }

  // Lookup helpers for impersonated member
  var impMember = null;
  for (var ti = 0; ti < TEAM.length; ti++) {
    if (TEAM[ti].email === impersonateEmail) { impMember = TEAM[ti]; break; }
  }
  var impName = impMember ? impMember.name : (impersonateEmail ? impersonateEmail.split('@')[0] : '');
  var impRole = impMember ? impMember.role : '';
  var isAaron = user && user.email === AARON_EMAIL;

  var userName = user ? (user.display_name || 'User') : '';
  var userInitials = userName.split(' ').map(function(n) { return n[0]; }).join('').substring(0, 2) || 'U';
  var firstName = userName ? userName.split(' ')[0] : '';
  var greeting = getGreeting() + (firstName ? ', ' + firstName : '');
  var dateStr = getDateStr();

  // Mobile voice: full-bleed, no chrome
  var _isVoiceMobile = false;
  try {
    var _hash = window.location.pathname + (window.location.search || '');
    _isVoiceMobile = isMobile && (_hash === '/voice' || _hash.startsWith('/voice?'));
  } catch(e) {}
  if (_isVoiceMobile) {
    return (
      <ThemeContext.Provider value={theme}>
        <UiModeContext.Provider value={uiMode}>
          <div style={{ minHeight: '100vh' }}>
            {children}
          </div>
        </UiModeContext.Provider>
      </ThemeContext.Provider>
    );
  }

  // Senior mode: simplified layout, no sidebar, large text, voice-first
  if (uiMode === 'senior') {
    return (
      <ThemeContext.Provider value={theme}>
        <UiModeContext.Provider value={uiMode}>
          <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: FONT }}>
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid ' + theme.border,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: theme.surface
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, fontFamily: FONT }}>
                {greeting}
              </div>
              <button
                onClick={function() { window.dispatchEvent(new CustomEvent('hub:trigger-mic')); }}
                style={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: theme.accent, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}
                title="Voice input"
              >
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x={9} y={1} width={6} height={12} rx={3} />
                  <path d="M19 10v2a7 7 0 01-14 0v-2" />
                  <line x1={12} y1={19} x2={12} y2={23} />
                </svg>
              </button>
            </div>
            <div style={{ padding: '20px', maxWidth: 600, margin: '0 auto' }}>
              <HomeBrief />
            </div>
          </div>
        </UiModeContext.Provider>
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={theme}>
      <UiModeContext.Provider value={uiMode}>
    <div style={{ display: 'flex', height: '100vh', fontFamily: FONT, background: theme.bg }}>

      {/* Sidebar -- hidden on admin routes and in popout mode */}
      {!location.pathname.startsWith('/platform') && !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/settings') && !(function() {
        try { return new URLSearchParams(window.location.search || '').get('popout') === 'true'; } catch(e) { return false; }
      })() && (
        <div style={isMobile ? {} : {
          width: sidebarMode === 'open' ? 220 : (sidebarMode === 'rail' ? 48 : 0),
          overflow: 'hidden',
          flexShrink: 0,
          transition: 'width 0.2s ease',
          opacity: sidebarMode === 'closed' ? 0 : 1
        }}>
          <Sidebar
            user={user}
            prefs={prefs}
            currentPath={location.pathname}
            onNavigate={function(path) { navigate(path); }}
            open={sidebarOpen}
            onClose={function() { setSidebarMode(isMobile ? 'closed' : 'rail'); }}
            isMobile={isMobile}
            railMode={sidebarMode === 'rail'}
            onAskHub={function() { search.setOpen(true); }}
          />
        </div>
      )}

      {/* Main area */}
      <div key={displayVer} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, minHeight: 0 }} className="hub-main">

        {/* Topbar -- 52px */}
        <div style={{
          height: 52, borderBottom: '1px solid ' + theme.border,
          display: 'flex', alignItems: 'center', padding: '0 20px',
          justifyContent: 'space-between', background: theme.topbar,
          flexShrink: 0
        }} className="hub-topbar">

          {/* Left: hamburger + greeting + date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={function() {
              setSidebarMode(function(prev) {
                if (window.innerWidth <= 768) {
                  return prev === 'open' ? 'closed' : 'open';
                }
                return prev === 'open' ? 'rail' : 'open';
              });
            }} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: theme.sidebarText, display: 'flex', alignItems: 'center'
            }} className="hub-sidebar-toggle" title="Toggle sidebar (Cmd+.)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, lineHeight: 1.3, fontFamily: FONT }}>{greeting}</div>
              <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.3, fontFamily: FONT }}>{dateStr}</div>
            </div>
          </div>

          {/* Right: search trigger + theme toggle + bell + avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Cmd+K search trigger -- hidden on mobile */}
            {!isMobile && (
              <button onClick={function() { search.setOpen(true); }} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: theme.surfaceInset, border: '1px solid ' + theme.border,
                borderRadius: 8, padding: '5px 12px', cursor: 'pointer',
                color: theme.textMuted, fontSize: 12, minWidth: 180, fontFamily: FONT
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <span style={{ flex: 1, textAlign: 'left' }}>Ask Hub...</span>
                <kbd style={{
                  fontSize: 10, fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                  background: theme.surface, border: '1px solid ' + theme.border,
                  borderRadius: 4, padding: '1px 5px', color: theme.textMuted
                }}>K</kbd>
              </button>
            )}

            {/* Theme toggle */}
            <button onClick={toggleTheme} style={{
              background: theme.surfaceInset, border: '1px solid ' + theme.border,
              borderRadius: 8, padding: isMobile ? '5px 8px' : '5px 10px', cursor: 'pointer',
              fontSize: 11, fontWeight: 500, color: theme.textSecondary,
              display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT
            }}>
              {themeName === 'dark' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                </svg>
              )}
              {!isMobile && (themeName === 'dark' ? 'Light' : 'Dark')}
            </button>

            {/* Voice Intake shortcut */}
            <button onClick={function() { window.dispatchEvent(new CustomEvent('hub:trigger-mic')); }} style={{
              background: theme.surfaceInset,
              border: '1px solid ' + theme.border,
              borderRadius: 8, padding: '5px 8px', cursor: 'pointer',
              color: theme.textSecondary,
              display: 'flex', alignItems: 'center'
            }} title="Voice Intake">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                <path d="M19 10v2a7 7 0 01-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>

            {/* Call Whisperer toggle */}
            <button onClick={function() { setWhispererActive(!whispererActive); }} style={{
              background: whispererActive ? (theme.accent + '18') : theme.surfaceInset,
              border: '1px solid ' + (whispererActive ? theme.accent : theme.border),
              borderRadius: 8, padding: '5px 8px', cursor: 'pointer',
              color: whispererActive ? theme.accent : theme.textSecondary,
              display: 'flex', alignItems: 'center'
            }} title="Call Whisperer">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </button>

            {/* Notification bell */}
            <NotificationBell user={user} />

            {/* Workspace switcher -- shown when multiple tenants available */}
            {tenants.length > 1 && (
              <div style={{ position: 'relative' }}>
                {tenantOpen && (
                  <div onClick={function() { setTenantOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 190 }} />
                )}
                <button
                  onClick={function() { setTenantOpen(!tenantOpen); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: currentTenant !== 'sprint_mode' ? (theme.accent + '18') : theme.surfaceInset,
                    border: '1px solid ' + (currentTenant !== 'sprint_mode' ? theme.accent : theme.border),
                    borderRadius: 8, padding: isMobile ? '4px 8px' : '4px 10px',
                    cursor: 'pointer',
                    color: currentTenant !== 'sprint_mode' ? theme.accent : theme.textSecondary,
                    fontSize: 11, fontWeight: 500, fontFamily: FONT
                  }}
                  title="Switch workspace"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                  {!isMobile && (function() {
                    var found = null;
                    for (var ti = 0; ti < tenants.length; ti++) { if (tenants[ti].tenant_id === currentTenant) { found = tenants[ti]; break; } }
                    return found ? (found.name || currentTenant) : currentTenant;
                  })()}
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="6,9 12,15 18,9"/>
                  </svg>
                </button>
                {tenantOpen && (
                  <div style={{
                    position: 'absolute', top: 34, right: 0, zIndex: 200,
                    width: 190, background: theme.surface,
                    border: '1px solid ' + theme.border,
                    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    padding: '4px'
                  }}>
                    {tenants.map(function(t) {
                      var isActive = currentTenant === t.tenant_id;
                      return (
                        <div
                          key={t.tenant_id}
                          onClick={function() { switchTenant(t.tenant_id); }}
                          style={{
                            padding: '7px 10px', fontSize: 12, cursor: 'pointer',
                            borderRadius: 6, fontFamily: FONT,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: isActive ? (theme.accent + '15') : 'transparent',
                            color: theme.text
                          }}
                        >
                          <span style={{ fontWeight: isActive ? 600 : 400 }}>{t.name || t.tenant_id}</span>
                          {isActive && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="2.5" strokeLinecap="round">
                              <polyline points="20,6 9,17 4,12"/>
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Impersonate dropdown -- Aaron only */}
            {isAaron && (
              <div style={{ position: 'relative' }}>
                {impOpen && (
                  <div onClick={function() { setImpOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 190 }} />
                )}
                <button
                  onClick={function() { setImpOpen(!impOpen); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: impersonateEmail ? (theme.accent + '18') : theme.surfaceInset,
                    border: '1px solid ' + (impersonateEmail ? theme.accent : theme.border),
                    borderRadius: 8, padding: isMobile ? '4px 8px' : '4px 10px',
                    cursor: 'pointer',
                    color: impersonateEmail ? theme.accent : theme.textSecondary,
                    fontSize: 11, fontWeight: 500, fontFamily: FONT
                  }}
                  title="View Hub as another team member"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {!isMobile && (impersonateEmail ? impName : 'View as')}
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="6,9 12,15 18,9"/>
                  </svg>
                </button>
                {impOpen && (
                  <div style={{
                    position: 'absolute', top: 34, right: 0, zIndex: 200,
                    width: 190, background: theme.surface,
                    border: '1px solid ' + theme.border,
                    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    padding: '4px'
                  }}>
                    {TEAM.map(function(member) {
                      var isActive = impersonateEmail === member.email || (!impersonateEmail && member.email === AARON_EMAIL);
                      return (
                        <div
                          key={member.email}
                          onClick={function() { handleImpersonate(member.email); }}
                          style={{
                            padding: '7px 10px', fontSize: 12, cursor: 'pointer',
                            borderRadius: 6, fontFamily: FONT,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: isActive ? (theme.accent + '15') : 'transparent',
                            color: theme.text
                          }}
                        >
                          <span>
                            <span style={{ fontWeight: 500 }}>{member.name}</span>
                            <span style={{ color: theme.textMuted, marginLeft: 6, fontSize: 10 }}>{member.role}</span>
                          </span>
                          {isActive && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.accent} strokeWidth="2.5" strokeLinecap="round">
                              <polyline points="20,6 9,17 4,12"/>
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Avatar with dropdown */}
            {user && (
              <div style={{ position: 'relative' }}>
                {/* Transparent overlay to close dropdown on outside click */}
                {avatarOpen && (
                  <div
                    onClick={function() { setAvatarOpen(false); }}
                    style={{ position: 'fixed', inset: 0, zIndex: 190 }}
                  />
                )}
                <div
                  onClick={function() { setAvatarOpen(!avatarOpen); }}
                  style={{
                    position: 'relative', width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: 'linear-gradient(135deg, ' + (theme.accent || '#4F6EF7') + ', #8B5CF6)',
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', userSelect: 'none', fontFamily: FONT
                  }}
                  title={userName}
                >
                  {userInitials}
                  {alertCounts.dev > 0 && (
                    <div style={{ position: 'absolute', top: -2, right: -2, width: 10, height: 10, borderRadius: '50%', background: '#EF4444', border: '2px solid ' + (theme.bg || '#f8f7f4') }} />
                  )}
                </div>
                {avatarOpen && (
                  <UserMenu
                    theme={theme}
                    user={user}
                    tenants={tenants}
                    currentTenant={currentTenant}
                    onSwitchTenant={switchTenant}
                    hubList={hubList}
                    currentHub={currentHub}
                    onSwitchHub={switchHub}
                    onClose={function() { setAvatarOpen(false); }}
                    navigate={navigate}
                    alertCounts={alertCounts}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Impersonate banner */}
        {impersonateEmail && (
          <div style={{
            background: theme.accent + '18',
            borderBottom: '1px solid ' + theme.accent + '40',
            padding: '5px 20px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            height: 32, boxSizing: 'border-box'
          }}>
            <span style={{ fontSize: 12, color: theme.accent, fontWeight: 500, fontFamily: FONT }}>
              {'Viewing as ' + impName + ' (' + impRole + ')'}
            </span>
            <button
              onClick={function() { handleImpersonate(''); }}
              style={{
                background: 'none', border: '1px solid ' + theme.accent + '60',
                borderRadius: 6, padding: '1px 10px', cursor: 'pointer',
                color: theme.accent, fontSize: 11, fontWeight: 600, fontFamily: FONT
              }}
            >
              Back to Aaron
            </button>
          </div>
        )}

        {/* Global status banner -- shows when any critical service is degraded */}
        {statusData && statusData.overall && statusData.overall !== 'operational' && !statusDismissed && (function() {
          var isOutage = statusData.overall === 'major_outage';
          var accentColor = isOutage ? '#EF4444' : '#F59E0B';
          var bgColor = isOutage ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)';
          var borderColor = isOutage ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)';
          var mutedColor = isOutage ? '#FCA5A5' : '#FCD34D';
          var dismissBg = isOutage ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)';
          var FRIENDLY = { hub_api: 'API', hub_pages: 'Dashboard', anthropic: 'AI' };
          var affectedServices = Array.isArray(statusData.services)
            ? statusData.services.filter(function(s) { return s.status === 'down' || s.status === 'degraded'; })
            : [];
          return (
            <div style={{
              borderBottom: '1px solid ' + borderColor,
              borderLeft: '4px solid ' + accentColor,
              background: bgColor,
              fontFamily: FONT, flexShrink: 0
            }}>
              {/* Collapsed row */}
              <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="8" cy="8" r="7" stroke={accentColor} strokeWidth="1.5"/>
                  <line x1="8" y1="4.5" x2="8" y2="8.5" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="8" cy="11" r="0.75" fill={accentColor}/>
                </svg>
                {/* Clickable text area toggles expanded */}
                <div
                  style={{ flex: 1, cursor: 'pointer', userSelect: 'none' }}
                  onClick={function() { setStatusExpanded(function(v) { return !v; }); }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: accentColor }}>
                    {isOutage ? 'Service disruption' : 'Some services degraded'}
                  </span>
                  {affectedServices.length > 0 && (
                    <span style={{ fontSize: 12, color: mutedColor, marginLeft: 8 }}>
                      {'Affected: ' + affectedServices.map(function(s) { return FRIENDLY[s.name] || s.name; }).join(', ')}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: accentColor, marginLeft: 8, opacity: 0.7 }}>
                    {statusExpanded ? '(collapse)' : '(details)'}
                  </span>
                </div>
                <a href="https://status.sprintmode.co" target="_blank" rel="noopener noreferrer" style={{
                  fontSize: 11, fontWeight: 600, textDecoration: 'none',
                  padding: '4px 10px', borderRadius: 6, flexShrink: 0,
                  background: dismissBg, color: accentColor
                }}>Status page</a>
                {/* Dismiss button */}
                <button
                  onClick={function() {
                    try { sessionStorage.setItem('hub_status_dismissed', '1'); } catch(e) {}
                    setStatusDismissed(true);
                  }}
                  title="Dismiss"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '2px 6px', borderRadius: 4, color: accentColor,
                    fontSize: 14, lineHeight: 1, flexShrink: 0, opacity: 0.7,
                    fontFamily: FONT
                  }}
                >x</button>
              </div>
              {/* Expanded service detail */}
              {statusExpanded && affectedServices.length > 0 && (
                <div style={{
                  padding: '0 20px 10px 46px',
                  display: 'flex', flexDirection: 'column', gap: 4
                }}>
                  {affectedServices.map(function(s) {
                    return (
                      <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: accentColor, fontWeight: 500 }}>
                          {FRIENDLY[s.name] || s.name}
                        </span>
                        <span style={{ fontSize: 11, color: mutedColor, textTransform: 'capitalize' }}>
                          {s.status}
                        </span>
                        {s.last_check && (
                          <span style={{ fontSize: 11, color: mutedColor, opacity: 0.7 }}>
                            {'last check: ' + s.last_check}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Content area -- full-bleed routes or standard padded wrapper */}
        {(function() {
          var fullBleedPaths = ['/brief', '/platform', '/admin', '/settings', '/agent/', '/agent-network', '/clone', '/central-legacy', '/agency', '/narrative', '/god-mode', '/voice'];
          return fullBleedPaths.some(function(p) { return location.pathname.startsWith(p); });
        })() ? (
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            <div style={{ maxWidth: 940, margin: '0 auto' }}>
              <ErrorBoundary>
                {children}
              </ErrorBoundary>
            </div>
          </div>
        )}
      </div>

      {/* Ask Hub drawer */}
      {askOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
          background: theme.card, borderLeft: '1px solid ' + theme.border,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)', zIndex: 300,
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + theme.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: theme.text, fontFamily: FONT }}>Ask Hub</span>
            <button onClick={function() { setAskOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: theme.textMuted }}>x</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <AgentOverlay />
          </div>
        </div>
      )}

      <GodMode />

      <CallWhisperer active={whispererActive} callId={activeCallId} onClose={function() { setWhispererActive(false); }} />

      {!isChromelessRoute && (
        <DisplayDrawer
          theme={theme}
          user={user}
          initialSettings={(function() {
            if (!prefs || !prefs.display_settings) return null;
            try { return typeof prefs.display_settings === 'string' ? JSON.parse(prefs.display_settings) : prefs.display_settings; } catch(e) { return null; }
          })()}
        />
      )}

      {/* Search / Cmd+K overlay */}
      <SearchOverlay
        open={search.open}
        onClose={function() { search.setOpen(false); }}
        onNavigate={function(url) { navigate(url); search.setOpen(false); }}
        userEmail={impersonateEmail || (user && user.email)}
      />

      {/* Bug Panel toggle -- bottom right. Suppressed on chromeless routes. */}
      {!isChromelessRoute && (
        <button
          onClick={function() { setBugPanelOpen(!bugPanelOpen); }}
          title={bugPanelOpen ? 'Close Bug Panel' : 'Open Bug Panel'}
          style={{
            position: 'fixed', bottom: 20, right: 20, zIndex: 9998,
            width: 44, height: 44, borderRadius: '50%',
            background: bugPanelOpen ? '#EF4444' : '#4F6EF7',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s'
          }}>
          {bugPanelOpen ? (
            <span style={{ color: '#fff', fontSize: 22, lineHeight: 1, fontFamily: 'sans-serif' }}>x</span>
          ) : (
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx={12} cy={12} r={10} />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          )}
        </button>
      )}

      {!isChromelessRoute && <BugPanel visible={bugPanelOpen} onClose={function() { setBugPanelOpen(false); }} />}

      {/* Flash toast */}
      {flashMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#0F172A', color: '#fff', padding: '10px 20px', borderRadius: 8,
          fontSize: 13, fontWeight: 500, zIndex: 500, pointerEvents: 'none',
          fontFamily: FONT
        }}>{flashMsg}</div>
      )}

      {/* Compose modal */}
      {composeData && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 400,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={function() { setComposeData(null); }}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{
            background: theme.card, borderRadius: 12, width: 560, maxHeight: '80vh',
            overflow: 'auto', padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid ' + theme.border
          }} className="hub-modal">
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 16, color: theme.text, fontFamily: FONT }}>Compose Email</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary, marginBottom: 4, fontFamily: FONT }}>To</label>
              <input value={cTo} onChange={function(e) { setCTo(e.target.value); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid ' + theme.border, borderRadius: 6, fontSize: 14, background: theme.bgSecondary, color: theme.text, boxSizing: 'border-box', fontFamily: FONT }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary, marginBottom: 4, fontFamily: FONT }}>Subject</label>
              <input value={cSubj} onChange={function(e) { setCSubj(e.target.value); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid ' + theme.border, borderRadius: 6, fontSize: 14, background: theme.bgSecondary, color: theme.text, boxSizing: 'border-box', fontFamily: FONT }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary, marginBottom: 4, fontFamily: FONT }}>Body</label>
              <textarea value={cBody} onChange={function(e) { setCBody(e.target.value); }} rows={8} style={{ width: '100%', padding: '8px 12px', border: '1px solid ' + theme.border, borderRadius: 6, fontSize: 14, resize: 'vertical', background: theme.bgSecondary, color: theme.text, boxSizing: 'border-box', fontFamily: FONT }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={function() { setComposeData(null); }} style={{ padding: '8px 16px', border: '1px solid ' + theme.border, borderRadius: 6, background: theme.card, color: theme.text, cursor: 'pointer', fontSize: 13, fontFamily: FONT }}>Cancel</button>
              <button onClick={handleComposeSend} disabled={cSending || !cTo || !cSubj} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: theme.blue, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: (cSending || !cTo || !cSubj) ? 0.6 : 1, fontFamily: FONT }}>{cSending ? 'Sending...' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
      <HelpPill />
      </UiModeContext.Provider>
    </ThemeContext.Provider>
  );
}
